import { Country, getCountries } from "@services/countries";
import { clearFacilitySessionSelectionForCountryChange } from "./facilityUtils";

const COUNTRIES_SESSION_KEY = "app_countries_data";

// Memory cache for countries data to avoid multiple API calls
let countriesCache: Country[] | null = null;

/**
 * Get the selected country code from localStorage
 * Defaults to "USA" if not found
 */
export function getSelectedCountryCode(): string {
  try {
    return localStorage.getItem("selectedCountry") || "USA";
  } catch {
    return "USA";
  }
}

/**
 * Historic penalties / enforcement search in this product is backed by US federal
 * and selected US state datasets only (not Canada, UAE, etc.).
 */
export function isUnitedStatesCountryCode(
  code: string | null | undefined,
): boolean {
  const u = String(code ?? "").trim().toUpperCase();
  return u === "USA" || u === "US";
}

/**
 * Load countries into cache from sessionStorage or API
 */
async function loadCountriesCache(): Promise<Country[]> {
  // First check memory cache
  if (countriesCache) {
    console.log("📦 Using memory cached countries");
    return countriesCache;
  }
  
  // Then check sessionStorage
  try {
    const sessionData = sessionStorage.getItem(COUNTRIES_SESSION_KEY);
    if (sessionData) {
      countriesCache = JSON.parse(sessionData);
      console.log("📦 Loaded", countriesCache!.length, "countries from sessionStorage");
      return countriesCache!;
    }
  } catch (error) {
    console.warn("⚠️ Failed to parse countries from sessionStorage:", error);
  }
  
  // Finally, fetch from API
  try {
    const response = await getCountries();
    countriesCache = response.countries;
    
    // Store in sessionStorage for the session
    try {
      sessionStorage.setItem(COUNTRIES_SESSION_KEY, JSON.stringify(countriesCache));
      console.log("✅ Loaded", countriesCache.length, "countries from API and cached in session");
    } catch (storageError) {
      console.warn("⚠️ Failed to cache countries in sessionStorage:", storageError);
    }
    
    return countriesCache;
  } catch (error) {
    console.error("❌ Error loading countries from API:", error);
    throw error; // Let the caller handle the error
  }
}

/**
 * Get the selected country name from localStorage (sync version)
 * Uses cached data dynamically from API
 */
export function getSelectedCountryName(): string {
  try {
    const countryCode = getSelectedCountryCode();
    
    // Use cached data from API
    if (countriesCache) {
      const country = countriesCache.find(c => c.country_code === countryCode);
      if (country) return country.country_name;
    }
    
    // If cache not loaded yet, return default
    // Cache will be initialized by SpacesNavigation on app load
    return "United States";
  } catch {
    return "United States";
  }
}

/** Minimal shape for filtering preferred facilities by country without importing payment types. */
export type CountryMatchablePreferred = {
  country?: string | null;
  facility_details?: { country?: string | null } | null;
};

/**
 * Normalize free-text or ISO country hints to a 3-letter code used in this app (USA, IND, …).
 */
export function canonicalCountryCodeFromHint(
  raw: string | null | undefined,
): string {
  const t = String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/\./g, "");
  if (!t) return "";
  if (t === "US" || t === "USA" || t.includes("UNITED STATES") || t === "AMERICA") {
    return "USA";
  }
  if (t === "IN" || t === "IND" || t.includes("INDIA")) {
    return "IND";
  }
  if (t === "CA" || t === "CAN" || t.includes("CANADA")) {
    return "CAN";
  }
  if (t === "AE" || t === "UAE" || t === "ARE" || t.includes("EMIRATES")) {
    return "UAE";
  }
  if (/^[A-Z]{3}$/.test(t)) {
    return t;
  }
  return t;
}

/** Country for a preferred-facility row; legacy rows without country infer USA. */
export function resolvePreferredFacilityCountryCode(
  f: CountryMatchablePreferred,
): string {
  const top = canonicalCountryCodeFromHint(f.country ?? null);
  if (top) return top;
  const d = f.facility_details?.country;
  const fromDetails = canonicalCountryCodeFromHint(d ?? null);
  if (fromDetails) return fromDetails;
  return "USA";
}

export function userPreferredFacilityMatchesCountryCode(
  f: CountryMatchablePreferred,
  selectedCode: string,
): boolean {
  const want =
    canonicalCountryCodeFromHint(selectedCode) ||
    String(selectedCode).trim().toUpperCase();
  return resolvePreferredFacilityCountryCode(f) === want;
}

/** Minimal shape for filtering user-facility rows from the Rules API by country. */
export type CountryMatchableUserFacility = {
  country_code?: string | null;
};

/**
 * Whether a user-associated facility belongs on the list for the selected country.
 * Legacy US rows may omit ``country_code`` — treat those as USA-only. For any other
 * selected country, require an explicit matching ``country_code`` so India does not
 * inherit a US facility when the API leaves the field blank.
 */
export function userFacilityMatchesSelectedCountryCode(
  f: CountryMatchableUserFacility,
  selectedCode: string,
): boolean {
  const want =
    canonicalCountryCodeFromHint(selectedCode) ||
    String(selectedCode).trim().toUpperCase();
  const cc = (f.country_code || "").trim().toUpperCase();
  const wantUs = want === "USA" || want === "US";
  if (wantUs) {
    if (!cc) return true;
    return cc === "USA" || cc === "US";
  }
  if (!cc) return false;
  return cc === want;
}

/**
 * Set the selected country code in localStorage
 */
export function setSelectedCountryCode(countryCode: string): void {
  try {
    clearFacilitySessionSelectionForCountryChange();
    localStorage.setItem("selectedCountry", countryCode);
    try {
      window.dispatchEvent(
        new CustomEvent("appCountryChanged", {
          detail: { countryCode: countryCode.trim() },
        }),
      );
    } catch {
      /* ignore */
    }
  } catch (error) {
    console.error("Failed to save country preference:", error);
  }
}

/**
 * Initialize countries cache on app load
 * Call this early in your app lifecycle (e.g., App.tsx useEffect)
 */
export async function initializeCountriesCache(): Promise<void> {
  try {
    await loadCountriesCache();
    console.log("✅ Countries cache initialized");
  } catch (error) {
    console.error("❌ Failed to initialize countries cache:", error);
  }
}

/**
 * Clear countries cache (call on logout)
 */
export function clearCountriesCache(): void {
  try {
    countriesCache = null;
    sessionStorage.removeItem(COUNTRIES_SESSION_KEY);
    console.log("🗑️ Countries cache cleared");
  } catch (error) {
    console.error("Failed to clear countries cache:", error);
  }
}

/**
 * Preload countries on login
 * Call this immediately after successful login
 */
export async function preloadCountriesOnLogin(): Promise<void> {
  try {
    console.log("🔄 Preloading countries on login...");
    await loadCountriesCache();
    console.log("✅ Countries preloaded successfully");
  } catch (error) {
    console.error("❌ Failed to preload countries on login:", error);
    throw error;
  }
}

/**
 * Default billing currency (ISO 4217) for the app's selected country.
 */
export function getDefaultCurrencyForCountryCode(countryCode: string): string {
  const c = String(countryCode || "USA")
    .trim()
    .toUpperCase();
  if (c === "US" || c === "USA") return "USD";
  if (c === "CA" || c === "CAN") return "CAD";
  if (c === "AE" || c === "UAE") return "AED";
  if (c === "IN" || c === "IND") return "INR";
  if (c === "GB" || c === "UK") return "GBP";
  return "USD";
}

/**
 * Values merged into pricing-tiers `filters` JSON for the current sidebar country.
 * Only `currency` is sent — MCM `/plans` rejects or mishandles some `country` slugs
 * (e.g. canada), which caused empty/error responses for Canada.
 */
export function getPricingLocaleFiltersForApi(): { currency: string } {
  const code = getSelectedCountryCode();
  return {
    currency: getDefaultCurrencyForCountryCode(code),
  };
}

